import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjJob, PbjJobMappingRow, PbjJobsSaveBody, PbjJobsSaveResult } from "../../../utils/models";
import { pbjCachedQueryOptions } from "./pbjQueryOptions";

export const PBJ_JOBS_QUERY_KEY = "pbj-jobs";

/** Role_string → PBJ job mapping rows for a department (the mapping grid source). */
export const usePbjJobMapping = (departmentId: string) =>
	useQuery<PbjJobMappingRow[]>({
		queryKey: [PBJ_JOBS_QUERY_KEY, "mapping", departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjJobMapping(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
		// Dept config, only mutated via useSavePbjJobs (setQueryData/invalidate) — never auto-refetch.
		...pbjCachedQueryOptions,
	});

/** The department's PBJ job registry (the dropdown source + Manage Jobs grid). */
export const usePbjJobs = (departmentId: string) =>
	useQuery<PbjJob[]>({
		queryKey: [PBJ_JOBS_QUERY_KEY, "registry", departmentId],
		queryFn: async () => {
			const res = await backendService.getPbjJobs(departmentId);
			return res.data;
		},
		enabled: !!departmentId,
		// Dept config, only mutated via useSavePbjJobs (setQueryData/invalidate) — never auto-refetch.
		...pbjCachedQueryOptions,
	});

/**
 * Save the whole Jobs-tab changeset. On success refreshes the current dept's caches from the result;
 * when applied to the network, invalidates every PBJ jobs cache since sibling depts may be cached.
 * Note: the block-on-delete error (409) is handled in the Manage Jobs dialog (it knows the
 * referencing role_strings); a generic error snackbar is the backstop here.
 */
export const useSavePbjJobs = (departmentId: string) => {
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<PbjJobsSaveResult, Error, PbjJobsSaveBody>({
		mutationFn: async (body) => {
			const res = await backendService.savePbjJobs(body);
			return res.data;
		},
		onSuccess: (result, body) => {
			queryClient.setQueryData<PbjJob[]>([PBJ_JOBS_QUERY_KEY, "registry", departmentId], result.jobs);
			queryClient.setQueryData<PbjJobMappingRow[]>([PBJ_JOBS_QUERY_KEY, "mapping", departmentId], result.mapping);
			if (body.applyToNetwork) {
				// Sibling departments were mutated too — drop their cached views so they refetch on next visit.
				queryClient.invalidateQueries({ queryKey: [PBJ_JOBS_QUERY_KEY] });
			}
			showSnackbar(t("pbj_model.jobs.saved", { count: result.affectedDepartmentIds.length }), "success");
			track("pbj_jobs_saved", {
				department_id: departmentId,
				apply_to_network: body.applyToNetwork,
				affected_count: result.affectedDepartmentIds.length,
				n_upserts: body.jobUpserts.length,
				n_deletes: body.jobDeletes.length,
				n_mapping_edits: body.mappingEdits.length,
			});
		},
		onError: (error) => {
			showSnackbar(t("pbj_model.jobs.save_failed"), "error");
			trackError("pbj_jobs_save_error", { error_message: error?.message });
		},
	});
};
